import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const STEPS = [
  { label: 'Cargando modelo IA', duration: 15 },
  { label: 'Procesando imágenes', duration: 35 },
  { label: 'Comparando rostros', duration: 60 },
  { label: 'Verificando identidad', duration: 90 },
];

const VerificacionFacialLoadingModal = ({ open }) => {
  const [seconds, setSeconds] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!open) {
      setSeconds(0);
      setActiveStep(0);
      return;
    }

    const interval = setInterval(() => {
      setSeconds((s) => {
        const next = s + 1;
        const step = STEPS.findLastIndex((st) => next >= st.duration);
        setActiveStep(Math.max(0, step + 1 < STEPS.length ? step + 1 : STEPS.length - 1));
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ className: 'rounded-2xl shadow-2xl overflow-hidden' }}
    >
      <DialogContent className="p-0">
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-8">

          {/* Icono central */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-400 rounded-full animate-ping opacity-20"></div>
              <div className="absolute inset-0 bg-blue-300 rounded-full animate-pulse opacity-30"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full p-7 shadow-2xl">
                <FingerprintIcon sx={{ fontSize: 56, color: 'white' }} className="animate-pulse" />
              </div>
              <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          </div>

          {/* Título + timer */}
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-1">
            Verificando Identidad
          </h2>
          <p className="text-center text-blue-600 font-mono text-lg font-semibold mb-5">
            {formatTime(seconds)}
          </p>

          {/* Barra de progreso indeterminada — no sabe cuándo termina, sigue animando */}
          <div className="mb-2">
            <div className="bg-white/70 backdrop-blur-sm rounded-full h-3 overflow-hidden shadow-inner">
              <div className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full animate-[shimmer_2s_ease-in-out_infinite]"
                style={{
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 2s ease-in-out infinite',
                  width: '60%',
                  marginLeft: 0,
                  animationName: 'indeterminate',
                }}
              />
            </div>
          </div>

          <style>{`
            @keyframes indeterminate {
              0%   { transform: translateX(-100%); width: 60%; }
              50%  { width: 80%; }
              100% { transform: translateX(200%); width: 60%; }
            }
          `}</style>

          {/* Pasos */}
          <div className="mt-5 space-y-2">
            {STEPS.map((step, i) => {
              const done = i < activeStep;
              const active = i === activeStep;
              return (
                <div
                  key={step.label}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-500 ${
                    done
                      ? 'bg-green-50 border border-green-200'
                      : active
                      ? 'bg-blue-50 border border-blue-300 shadow-sm'
                      : 'bg-white/40 border border-transparent opacity-40'
                  }`}
                >
                  {done ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: '#16a34a' }} />
                  ) : active ? (
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}
                  <span
                    className={`text-sm font-medium ${
                      done ? 'text-green-700' : active ? 'text-blue-700' : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Aviso de tiempo */}
          <div className="mt-5 bg-white/50 backdrop-blur-sm rounded-xl p-3 border border-blue-200">
            <p className="text-center text-xs text-gray-600">
              {seconds < 20
                ? 'Iniciando motor de reconocimiento facial con IA...'
                : seconds < 60
                ? 'Analizando puntos biométricos del rostro...'
                : 'El primer análisis puede tardar hasta 2 minutos. Los siguientes serán más rápidos.'}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificacionFacialLoadingModal;
